'use client'

import { fetchBalance } from '@/api/v2/queries/user/balance'
import Button from '@/components/design-system/button'
import ButtonGroup from '@/components/design-system/button-group'
import {
  Cascade,
  CascadeContent,
  CascadeSub,
  CascadeSubContent,
  CascadeSubTrigger,
} from '@/components/design-system/cascade'
import Collapse from '@/components/design-system/collapse'
import NumberFormat from '@/components/design-system/number-format'
import Tooltip from '@/components/design-system/tooltip'
import AirdropIcon from '@/components/icons/airdrop'
import BarsIcon from '@/components/icons/bars'
import CandlesIcon from '@/components/icons/candles'
import CardsIcon from '@/components/icons/cards'
import ChevronDownIcon from '@/components/icons/chevron-down'
import ChevronRightIcon from '@/components/icons/chevron-right'
import DicesIcon from '@/components/icons/dices'
import GiftIcon from '@/components/icons/gift'
import HandMoneyIcon from '@/components/icons/hand-money'
import HomeIcon from '@/components/icons/home'
import LanguageIcon from '@/components/icons/language'
import MicIcon from '@/components/icons/mic'
import Mic2Icon from '@/components/icons/mic2'
import NftIcon from '@/components/icons/nft'
import PigIcon from '@/components/icons/pig'
import QuestionBalloonIcon from '@/components/icons/question-balloon'
import RouletteIcon from '@/components/icons/roulette'
import SlotsMachineIcon from '@/components/icons/slots-machine'
import AlpineSkiingIcon from '@/components/icons/sports/alpine-skiing'
import AmericanFootballIcon from '@/components/icons/sports/american-football'
import AutoRacingIcon from '@/components/icons/sports/auto-racing'
import BadmintonIcon from '@/components/icons/sports/badminton'
import BandyIcon from '@/components/icons/sports/bandy'
import BaseballIcon from '@/components/icons/sports/baseball'
import BasketballIcon from '@/components/icons/sports/basketball'
import BoxingIcon from '@/components/icons/sports/boxing'
import ChessIcon from '@/components/icons/sports/chess'
import CricketIcon from '@/components/icons/sports/cricket'
import CyclingIcon from '@/components/icons/sports/cycling'
import DartsIcon from '@/components/icons/sports/darts'
import F1Icon from '@/components/icons/sports/f1'
import FieldHockeyIcon from '@/components/icons/sports/field-hockey'
import FloorballIcon from '@/components/icons/sports/floorball'
import FreestyleWrestlingIcon from '@/components/icons/sports/freestyle-wrestling'
import FutsalIcon from '@/components/icons/sports/futsal'
import GaelicFootballIcon from '@/components/icons/sports/gaelic-football'
import GolfIcon from '@/components/icons/sports/golf'
import HandballIcon from '@/components/icons/sports/handball'
import HurlingIcon from '@/components/icons/sports/hurling'
import LacrosseIcon from '@/components/icons/sports/lacrosse'
import MMAIcon from '@/components/icons/sports/mma'
import MotorbikesIcon from '@/components/icons/sports/motorbikes'
import NascarIcon from '@/components/icons/sports/nascar'
import NetballIcon from '@/components/icons/sports/netball'
import PoliticsIcon from '@/components/icons/sports/politics'
import RugbyLeagueIcon from '@/components/icons/sports/rugby-league'
import RugbyUnionIcon from '@/components/icons/sports/rugby-union'
import SnookerIcon from '@/components/icons/sports/snooker'
import SoccerIcon from '@/components/icons/sports/soccer'
import SpeedwayIcon from '@/components/icons/sports/speedway'
import SquashIcon from '@/components/icons/sports/squash'
import TableTennisIcon from '@/components/icons/sports/table-tennis'
import TennisIcon from '@/components/icons/sports/tennis'
import TvShowsAndMoviesIcon from '@/components/icons/sports/tv-shows-and-movies'
import VolleyballIcon from '@/components/icons/sports/volleyball'
import WaterPoloIcon from '@/components/icons/sports/water-polo'
import TrendingIcon from '@/components/icons/trending'
import TrophyIcon from '@/components/icons/trophy'
import useAuth from '@/hooks/use-auth'
import { Link, usePathname } from '@/navigation'
import { PATH_CASINO, PATH_CRYPTO, PATH_SPORTS } from '@/routes/paths'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useContext, useState } from 'react'
import { LayoutContext } from '../layout-context'
import LanguagePanel from './language-panel'
import './sidebar.scss'

export const menu = [
  {
    title: 'categories.casino.title',
    icon: <HomeIcon />,
    children: [
      {
        href: PATH_CASINO.lobby,
        title: 'categories.casino.lobby',
        icon: <HomeIcon />,
      },
      {
        href: PATH_CASINO.originals,
        title: 'categories.casino.originals',
        icon: <PigIcon width={10} />,
      },
      {
        href: PATH_CASINO.slots,
        title: 'categories.casino.slots',
        icon: <SlotsMachineIcon />,
      },
      {
        href: PATH_CASINO.liveCasino,
        title: 'categories.casino.live-casino',
        icon: <MicIcon />,
      },
      {
        href: PATH_CASINO.gameShows,
        title: 'categories.casino.game-shows',
        icon: <Mic2Icon />,
      },

      {
        href: PATH_CASINO.tableGames,
        title: 'categories.casino.table-games',
        icon: <DicesIcon />,
      },
      {
        href: PATH_CASINO.roulette,
        title: 'categories.casino.roulette',
        icon: <RouletteIcon />,
      },
      {
        href: PATH_CASINO.blackjack,
        title: 'categories.casino.blackjack',
        icon: <CardsIcon />,
      },
      {
        href: PATH_CASINO.newReleases,
        title: 'categories.casino.new-releases',
        icon: <GiftIcon />,
      },
    ],
  },
  {
    title: 'categories.trade.title',
    icon: <TrendingIcon />,
    children: [
      {
        href: PATH_CRYPTO.futures,
        title: 'categories.trade.futures',
        icon: <CandlesIcon />,
      },
    ],
  },
  {
    title: 'categories.sports.title',
    icon: <TrophyIcon />,
    isDisabled: false,
    children: [
      {
        href: PATH_SPORTS.tableTennis,
        title: 'games.sports.table-tennis',
        icon: <TableTennisIcon />,
      },
      {
        href: PATH_SPORTS.volleyball,
        title: 'games.sports.volleyball',
        icon: <VolleyballIcon />,
      },
      {
        href: PATH_SPORTS.tennis,
        title: 'games.sports.tennis',
        icon: <TennisIcon />,
      },
      {
        href: PATH_SPORTS.soccer,
        title: 'games.sports.soccer',
        icon: <SoccerIcon />,
      },
      {
        href: PATH_SPORTS.basketball,
        title: 'games.sports.basketball',
        icon: <BasketballIcon />,
      },
      {
        href: PATH_SPORTS.rugbyLeague,
        title: 'games.sports.rugby-league',
        icon: <RugbyLeagueIcon />,
      },
      {
        href: PATH_SPORTS.rugbyUnion,
        title: 'games.sports.rugby-union',
        icon: <RugbyUnionIcon />,
      },
      {
        href: PATH_SPORTS.boxing,
        title: 'games.sports.boxing',
        icon: <BoxingIcon />,
      },
      {
        href: PATH_SPORTS.golf,
        title: 'games.sports.golf',
        icon: <GolfIcon />,
      },
      {
        href: PATH_SPORTS.mma,
        title: 'games.sports.mma',
        icon: <MMAIcon />,
      },
      {
        href: PATH_SPORTS.snooker,
        title: 'games.sports.snooker',
        icon: <SnookerIcon />,
      },
      {
        href: PATH_SPORTS.alpineSkiing,
        title: 'games.sports.alpine-skiing',
        icon: <AlpineSkiingIcon />,
      },
      // {
      //   href: PATH_SPORTS.iceHockey,
      //   title: 'games.sports.ice-hokey',
      //   icon: <IceHockeyIcon />,
      // },
      {
        href: PATH_SPORTS.americanFootball,
        title: 'games.sports.american-football',
        icon: <AmericanFootballIcon />,
      },
      {
        href: PATH_SPORTS.f1,
        title: 'games.sports.f1',
        icon: <F1Icon />,
      },
      {
        href: PATH_SPORTS.fieldHockey,
        title: 'games.sports.field-hockey',
        icon: <FieldHockeyIcon />,
      },
      {
        href: PATH_SPORTS.badminton,
        title: 'games.sports.badminton',
        icon: <BadmintonIcon />,
      },
      {
        href: PATH_SPORTS.baseball,
        title: 'games.sports.baseball',
        icon: <BaseballIcon />,
      },
      {
        href: PATH_SPORTS.cricket,
        title: 'games.sports.cricket',
        icon: <CricketIcon />,
      },
      {
        href: PATH_SPORTS.autoRacing,
        title: 'games.sports.auto-racing',
        icon: <AutoRacingIcon />,
      },
      {
        href: PATH_SPORTS.waterPolo,
        title: 'games.sports.water-polo',
        icon: <WaterPoloIcon />,
      },
      {
        href: PATH_SPORTS.chess,
        title: 'games.sports.chess',
        icon: <ChessIcon />,
      },
      {
        href: PATH_SPORTS.bandy,
        title: 'games.sports.bandy',
        icon: <BandyIcon />,
      },
      {
        href: PATH_SPORTS.cycling,
        title: 'games.sports.cycling',
        icon: <CyclingIcon />,
      },
      {
        href: PATH_SPORTS.darts,
        title: 'games.sports.darts',
        icon: <DartsIcon />,
      },
      {
        href: PATH_SPORTS.motorbikes,
        title: 'games.sports.motorbikes',
        icon: <MotorbikesIcon />,
      },
      {
        href: PATH_SPORTS.floorball,
        title: 'games.sports.floorball',
        icon: <FloorballIcon />,
      },
      {
        href: PATH_SPORTS.freestyleWrestling,
        title: 'games.sports.freestyle-wrestling',
        icon: <FreestyleWrestlingIcon />,
      },
      {
        href: PATH_SPORTS.handball,
        title: 'games.sports.handball',
        icon: <HandballIcon />,
      },
      {
        href: PATH_SPORTS.futsal,
        title: 'games.sports.futsal',
        icon: <FutsalIcon />,
      },
      {
        href: PATH_SPORTS.gaelicFootball,
        title: 'games.sports.gaelic-football',
        icon: <GaelicFootballIcon />,
      },
      {
        href: PATH_SPORTS.hurling,
        title: 'games.sports.hurling',
        icon: <HurlingIcon />,
      },
      {
        href: PATH_SPORTS.lacrosse,
        title: 'games.sports.lacrosse',
        icon: <LacrosseIcon />,
      },
      {
        href: PATH_SPORTS.nascar,
        title: 'games.sports.nascar',
        icon: <NascarIcon />,
      },
      {
        href: PATH_SPORTS.netball,
        title: 'games.sports.netball',
        icon: <NetballIcon />,
      },
      {
        href: PATH_SPORTS.politics,
        title: 'games.sports.politics',
        icon: <PoliticsIcon />,
      },
      {
        href: PATH_SPORTS.speedway,
        title: 'games.sports.speedway',
        icon: <SpeedwayIcon />,
      },
      {
        href: PATH_SPORTS.squash,
        title: 'games.sports.squash',
        icon: <SquashIcon />,
      },
      {
        href: PATH_SPORTS.tvShowsAndMovies,
        title: 'games.sports.tv-shows-and-movies',
        icon: <TvShowsAndMoviesIcon />,
      },
    ],
  },
]

export default function Sidebar() {
  const pathname = usePathname()
  const t = useTranslations()
  const { sidebarOpen, onSidebarOpenChange } = useContext(LayoutContext)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const { data } = useQuery(['balance'], fetchBalance)
  const { isAuthenticated } = useAuth()

  return (
    <div
      className={clsx('sidebar', `sidebar--${sidebarOpen ? 'open' : 'closed'}`)}
      role='navigation'
    >
      <div
        className='sidebar__overlay'
        onClick={() => {
          onSidebarOpenChange(false)
        }}
      />

      <Cascade className='sidebar__wrapper'>
        <CascadeContent className='sidebar__inner'>
          <div className='sidebar__content'>
            <section className='sidebar__section'>
              <div className='sidebar__section-content'>
                <div className='sidebar__balance'>
                  {/* eslint-disable @next/next/no-img-element */}
                  <img
                    alt='Pig Coin'
                    src='/assets/pig-coin.png'
                    height={40}
                    width={40}
                  />
                  {/* eslint-enable @next/next/no-img-element */}

                  <div>
                    <h3>Pigmo Coin</h3>
                    <span>
                      $
                      <NumberFormat
                        value={data?.pigBalance ?? 0}
                        style='decimal'
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                      />
                    </span>
                  </div>
                </div>
                <div
                  className={clsx(
                    `sidebar__section-inner-content--${sidebarOpen ? 'open' : 'closed'}`
                  )}
                >
                  <Tooltip
                    title='Blog'
                    placement='right'
                    disabled={sidebarOpen}
                  >
                    <Link href='/blog' className='sidebar__button'>
                      <HandMoneyIcon />
                      <span>Blog</span>
                    </Link>
                  </Tooltip>

                  <Tooltip
                    title='Dashboard'
                    placement='right'
                    disabled={sidebarOpen}
                  >
                    <Link href='/dashboard' className='sidebar__button'>
                      <BarsIcon />
                      <span>Dashboard</span>
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </section>

            <section className='sidebar__section'>
              <div className='sidebar__section-header'>
                <div className='sidebar__section-title'>Pigmo</div>
              </div>
              <div className='sidebar__section-content'>
                {/* <Tooltip
                  title='$PIG Airdrop'
                  placement='right'
                  disabled={sidebarOpen}
                >
                  <Link href='/airdrop' className='sidebar__button airdrop'>
                    <AirdropIcon />
                    <span>$PIG Airdrop</span>
                  </Link>
                </Tooltip>   */}
                <Tooltip
                  title='Bonus Deposit'
                  placement='right'
                  disabled={sidebarOpen}
                >
                  <Link
                    href={
                      isAuthenticated ? '/profile' : '?modal=auth&tab=login'
                    }
                    className='sidebar__button bonus-deposit'
                  >
                    <GiftIcon />
                    <span>Bonus Deposit</span>
                  </Link>
                </Tooltip>
                <Tooltip
                  title='NFT Staking'
                  placement='right'
                  disabled={sidebarOpen}
                >
                  <Link href='/staking' className='sidebar__button staking'>
                    <NftIcon />
                    <span>NFT Staking</span>
                  </Link>
                </Tooltip>
              </div>
            </section>

            <section className='sidebar__section'>
              <div className='sidebar__section-header'>
                <div className='sidebar__section-title'>Menu</div>
              </div>
              <div className='sidebar__section-content'>
                {menu.map((item, i) => (
                  <div key={i} className='sidebar__accordion'>
                    <Tooltip
                      title={t(item.title)}
                      placement='right'
                      disabled={sidebarOpen}
                    >
                      <button
                        className='sidebar__button'
                        disabled={item.isDisabled}
                        style={{
                          opacity: item.isDisabled ? 0.5 : 1,
                        }}
                        data-active={sidebarOpen && i === activeIndex}
                        onClick={() => {
                          onSidebarOpenChange(true)
                          setActiveIndex((prevIndex) =>
                            sidebarOpen && prevIndex === i ? null : i
                          )
                        }}
                      >
                        {item.icon}
                        <span>{t(item.title)}</span>
                        <div className='sidebar__chevron'>
                          <ChevronDownIcon />
                        </div>
                      </button>
                    </Tooltip>

                    <Collapse in={activeIndex === i}>
                      {item.children.map(({ href, title, icon }) => {
                        const isActive =
                          href === pathname ||
                          (pathname.startsWith('/crypto/futures') &&
                            href === PATH_CRYPTO.futures)

                        return (
                          <Link
                            key={href}
                            href={href}
                            className={clsx(
                              'sidebar__link',
                              isActive && 'sidebar__link--active'
                            )}
                          >
                            {icon}
                            {t(title)}

                            <span
                              className={`sidebar__indicator-${isActive ? 'active' : 'inactive'}`}
                            >
                              <span />
                            </span>
                          </Link>
                        )
                      })}
                    </Collapse>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className='sidebar__footer'>
            <ButtonGroup orientation='vertical'>
              <Tooltip title='FAQ' placement='right' disabled={sidebarOpen}>
                <Button
                  component={Link}
                  href='/faq'
                  variant='outline'
                  size='lg'
                  textAlign='left'
                  startIcon={<QuestionBalloonIcon color='primary' />}
                >
                  FAQ
                </Button>
              </Tooltip>

              <CascadeSub>
                <Tooltip
                  title='Language'
                  placement='right'
                  disabled={sidebarOpen}
                >
                  <Button
                    component={CascadeSubTrigger}
                    variant='outline'
                    size='lg'
                    textAlign='left'
                    startIcon={<LanguageIcon color='primary' />}
                    endIcon={<ChevronRightIcon />}
                  >
                    Language
                  </Button>
                </Tooltip>

                <CascadeSubContent className='sidebar__panel'>
                  <LanguagePanel />
                </CascadeSubContent>
              </CascadeSub>
            </ButtonGroup>
          </div>
        </CascadeContent>
      </Cascade>
    </div>
  )
}
